import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApartmentOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  ArrowRightOutlined,
  FileDoneOutlined,
  RadarChartOutlined,
} from '@ant-design/icons';
import { EstimateLink, SampleDownload } from '../../components/marketing/Marketing';
import './HomePage.css';

const processSteps = [
  {
    title: 'Вы описываете объект',
    text: 'Указываете адрес, площадь, назначение и кадастровые сведения.',
    image: '/images/process-object.png',
    alt: 'Объёмная схема коммерческого объекта с отметкой адреса',
  },
  {
    title: 'Проверяете данные',
    text: 'Дополняете характеристики и подтверждаете сведения перед расчётом.',
    image: '/images/process-data.png',
    alt: 'Проверенные кадастровые и технические данные объекта',
  },
  {
    title: 'Сравниваете с рынком',
    text: 'Изучаете подобранные аналоги, их расположение и параметры.',
    image: '/images/process-market.png',
    alt: 'Три сопоставимых бизнес-центра на схеме города',
  },
  {
    title: 'Получаете результат',
    text: 'Видите стоимость, расчётные основания и готовое заключение.',
    image: '/images/process-report.png',
    alt: 'Готовое заключение с графиком и расчётными материалами',
  },
];

const sampleOffers = [
  { name: 'Невский проспект, 74', rate: 2695, area: '1 850 м²', year: '2012', type: 'Бизнес-центр', distance: '0,4 км', image: '/images/market-building.png', alt: 'Современный бизнес-центр на Невском проспекте' },
  { name: 'Литейный проспект, 55', rate: 2718, area: '1 320 м²', year: '1910', type: 'Офисное здание', distance: '0,7 км', image: '/images/auth-architecture-v2.png', alt: 'Офисное здание на Литейном проспекте' },
  { name: 'Набережная Фонтанки, 40', rate: 2741, area: '980 м²', year: '1898', type: 'Особняк с офисами', distance: '1,1 км', image: '/images/value-building-photo.png', alt: 'Исторический коммерческий особняк на набережной Фонтанки' },
  { name: 'Владимирский проспект, 8', rate: 2764, area: '1 640 м²', year: '2006', type: 'Бизнес-центр', distance: '1,3 км', image: '/images/contacts-consultation.png', alt: 'Бизнес-центр на Владимирском проспекте' },
  { name: 'Улица Марата, 12', rate: 2787, area: '760 м²', year: '1905', type: 'Офисы и ритейл', distance: '1,5 км', image: '/images/home-city-hero-ultrawide.png', alt: 'Коммерческие здания в квартале улицы Марата' },
  { name: 'Улица Жуковского, 5', rate: 2810, area: '1 420 м²', year: '1908', type: 'Торговое помещение', distance: '1,7 км', image: '/images/architecture-section.webp', alt: 'Торговое помещение в историческом здании на улице Жуковского' },
];

const calculationStages = [
  { title: 'Реестр', text: 'границы и характеристики', icon: DatabaseOutlined, image: '/images/pipeline-registry.png', alt: 'Коммерческий объект на кадастровом плане с границами участка' },
  { title: 'Проверка', text: 'адрес и параметры', icon: RadarChartOutlined, image: '/images/pipeline-validation.png', alt: 'Архитектурная модель, документы и проверенные параметры объекта' },
  { title: 'Модель', text: 'объект и окружение', icon: ApartmentOutlined, image: '/images/pipeline-market.png', alt: 'Городской квартал с объектом и шестью рыночными аналогами' },
  { title: 'Заключение', text: 'стоимость и основания', icon: FileDoneOutlined, image: '/images/pipeline-report.png', alt: 'Готовое заключение об оценке и его цифровая версия' },
];

export default function HomePage() {
  const [comparisonPosition, setComparisonPosition] = useState(52);
  const [selectedOffer, setSelectedOffer] = useState(0);
  const [activeStage, setActiveStage] = useState(0);
  const pipelineRef = useRef(null);
  const currentOffer = sampleOffers[selectedOffer];

  useEffect(() => {
    if (!('IntersectionObserver' in window) || !window.matchMedia('(hover: none), (max-width: 900px)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let timer;
    const observer = new IntersectionObserver(([entry]) => {
      window.clearInterval(timer);
      if (entry.isIntersecting) timer = window.setInterval(() => setActiveStage((stage) => (stage + 1) % calculationStages.length), 2600);
    }, { threshold: .45 });
    if (pipelineRef.current) observer.observe(pipelineRef.current);
    return () => { observer.disconnect(); window.clearInterval(timer); };
  }, []);

  return (
    <main className="zema-public zema-home">
      <section className="zema-home-hero" aria-labelledby="zema-home-title">
        <img className="zema-home-hero-background" src="/images/home-city-hero-ultrawide.png" alt="" aria-hidden="true" fetchpriority="high" />
        <div className="zema-home-hero-inner zema-wrap">
          <div className="zema-home-hero-copy">
            <h1 id="zema-home-title">Рыночная стоимость <span>вашего объекта</span></h1>
            <p>Экспресс-оценка коммерческой недвижимости. Укажите адрес и характеристики — ЗЕМА найдёт сопоставимые предложения и покажет предварительную стоимость с объяснением расчёта.</p>
            <div className="zema-actions">
              <EstimateLink>Оценить объект</EstimateLink>
              <a className="zema-hero-example" href="#zema-result-preview">Посмотреть результат <ArrowRightOutlined /></a>
            </div>
            <dl className="zema-home-sources" aria-label="Параметры сервиса">
              <div><dt>от 15 минут</dt><dd>до предварительного расчёта</dd></div>
              <div><dt>Рыночные аналоги</dt><dd>на карте и в сравнении</dd></div>
              <div><dt>Экран + PDF</dt><dd>результат остаётся у вас</dd></div>
            </dl>
          </div>

          <div className="zema-home-hero-visual">
            <div className="zema-home-object-card">
              <div className="zema-home-object-card-head"><span>Пример расчёта</span><i>готов</i></div>
              <h2>Невский проспект, 68</h2>
              <p>Бизнес-центр · 1 850 м²</p>
              <div className="zema-home-object-price"><strong>294 000 000 ₽</strong><small>159 000 ₽ за м²</small></div>
              <a href="#zema-result-preview">Открыть результат <ArrowRightOutlined /></a>
              <div className="zema-home-map-note"><EnvironmentOutlined /><span>Санкт-Петербург<br /><strong>Центральный район</strong></span></div>
            </div>
          </div>
        </div>
      </section>

      <section className="zema-home-process zema-wrap" id="zema-how" aria-labelledby="zema-process-title">
        <div className="zema-home-section-heading">
          <div><h2 id="zema-process-title">Как ЗЕМА считает стоимость</h2></div>
          <p>Вы видите весь путь расчёта: какие сведения приняты, что попало в сравнение и почему получилась именно эта сумма.</p>
        </div>
        <div className="zema-process-grid">
          {processSteps.map((step, index) => (
            <article className={`zema-process-card zema-process-card--${index + 1}`} key={step.title}>
              <div className="zema-process-card-copy"><h3>{step.title}</h3><p>{step.text}</p></div>
              <div className="zema-process-card-visual">
                <img src={step.image} alt={step.alt} width="1024" height="1024" loading="lazy" />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section ref={pipelineRef} className="zema-home-pipeline" aria-labelledby="zema-pipeline-title">
        <div className="zema-home-pipeline-inner zema-wrap">
          <div className="zema-home-pipeline-copy">
            <h2 id="zema-pipeline-title">От кадастра до рыночной модели</h2>
            <p>ЗЕМА соединяет сведения об объекте, его положение в городе и реальные предложения. На выходе — не абстрактная цифра, а расчёт, который можно разобрать по источникам.</p>
            <div className="zema-pipeline-stages" aria-label="Состав расчёта">
              {calculationStages.map(({ title, text, icon: Icon }, index) => (
                <button type="button" key={title} aria-pressed={activeStage === index} onMouseEnter={() => setActiveStage(index)} onFocus={() => setActiveStage(index)} onClick={() => setActiveStage(index)}><Icon aria-hidden="true" /><span><strong>{title}</strong><small>{text}</small></span><i aria-hidden="true" /></button>
              ))}
            </div>
          </div>
          <figure className="zema-pipeline-model" data-stage={activeStage}>
            <img key={calculationStages[activeStage].image} src={calculationStages[activeStage].image} alt={calculationStages[activeStage].alt} width="1024" height="1024" loading="lazy" />
            <div className="zema-pipeline-status" role="status"><span>{calculationStages[activeStage].title}</span><strong>{['Получены границы и площадь', 'Сведения сверены', 'Собрано 6 аналогов', 'Расчёт готов'][activeStage]}</strong></div>
          </figure>
        </div>
      </section>

      <section className="zema-home-value" aria-labelledby="zema-value-title">
        <div className="zema-home-value-inner zema-wrap">
          <div className="zema-home-value-copy">
            <h2 id="zema-value-title">Стоимость, которую можно проверить</h2>
            <p>За стоимостью стоят характеристики здания и предложения рядом. Изучайте аналоги на карте, сравнивайте параметры и проверяйте логику расчёта.</p>
            <Link className="zema-button zema-button--light" to="/services">Посмотреть, что внутри <ArrowRightOutlined /></Link>
          </div>
          <figure className="zema-home-value-figure" style={{ '--comparison-position': `${comparisonPosition}%` }}>
            <div className="zema-value-comparison">
              <img className="zema-value-photo" src="/images/value-building-photo.png" alt="Историческое коммерческое здание у набережной Санкт-Петербурга" width="1672" height="941" loading="lazy" />
              <div className="zema-value-blueprint"><img src="/images/value-blueprint.png" alt="Цифровая модель того же здания с аналитическими связями" width="1672" height="941" loading="lazy" /></div>
              <span className="zema-value-label zema-value-label--photo">Объект</span>
              <span className="zema-value-label zema-value-label--model">Модель</span>
              <div className="zema-value-divider" aria-hidden="true"><span>‹ ›</span></div>
              <input type="range" min="12" max="88" value={comparisonPosition} onChange={(event) => setComparisonPosition(event.target.value)} aria-label="Сравнить фотографию объекта и цифровую модель" />
            </div>
            <figcaption className="zema-home-value-metrics"><strong>Проведите по фасаду</strong><span>Слева — реальный объект, справа — его цифровой двойник.</span></figcaption>
          </figure>
        </div>
      </section>

      <section className="zema-home-market zema-wrap" aria-labelledby="zema-market-title">
        <div className="zema-home-market-grid">
          <div className="zema-home-market-panel">
            <div className="zema-market-live-head"><div><span className="zema-live-dot" aria-hidden="true" /><small>Рыночная выборка</small></div><strong id="zema-market-title">6 предложений рядом</strong><p>Кликните по столбцу — карточка покажет адрес и ставку выбранного аналога.</p></div>
            <div className="zema-home-market-total">
              <span>Невский проспект, 68</span>
              <strong>294 000 000 ₽</strong>
              <small>159 000 ₽/м²</small>
            </div>
            <dl className="zema-home-market-facts">
              <div><dt>Площадь</dt><dd>{currentOffer.area}</dd></div>
              <div><dt>Год постройки</dt><dd>{currentOffer.year}</dd></div>
              <div><dt>Тип объекта</dt><dd>{currentOffer.type}</dd></div>
            </dl>
            <div className="zema-home-market-chart" aria-label="Пример сравнения ставок шести аналогов">
              <div className="zema-home-market-chart-head"><span>Ставки аналогов</span><small>₽/м²/мес.</small></div>
              <div className="zema-home-market-bars">
                {sampleOffers.map((offer, index) => <button key={offer.name} type="button" aria-pressed={selectedOffer === index} aria-label={`${offer.name}: ${offer.rate} рублей за м² в месяц`} onClick={() => setSelectedOffer(index)} style={{ '--bar': `${offer.rate / 32}%` }}><i aria-hidden="true" /></button>)}
              </div>
              <div className="zema-market-offer" role="status" key={selectedOffer}><span>{currentOffer.name}</span><strong>{currentOffer.rate.toLocaleString('ru-RU')} <small>₽/м²/мес.</small></strong></div>
              <p className="zema-market-update"><span className="zema-live-dot" aria-hidden="true" />Выборка пересчитана по выбранному аналогу</p>
            </div>
            <Link className="zema-text-link" to="/analytics"><BarChartOutlined /> Перейти к аналитике</Link>
          </div>
          <figure className="zema-home-market-image" data-selected-offer={selectedOffer}>
            <img key={currentOffer.image} src={currentOffer.image} alt={currentOffer.alt} width="1456" height="1092" loading="lazy" />
            <div className="zema-market-image-data" aria-label="Параметры выбранного аналога"><span><strong>{currentOffer.distance}</strong>от объекта</span><span><strong>{currentOffer.rate.toLocaleString('ru-RU')} ₽</strong>ставка</span><span><strong>{currentOffer.area}</strong>площадь</span></div>
            <figcaption key={currentOffer.name}><strong>{currentOffer.name}</strong><span>{currentOffer.type} · {currentOffer.rate.toLocaleString('ru-RU')} ₽/м²/мес.</span></figcaption>
          </figure>
        </div>
      </section>

      <div className="zema-home-result-flow">
        <img className="zema-home-result-flow-background" src="/images/architecture-section.webp" alt="" aria-hidden="true" loading="lazy" />
        <section className="zema-home-result" id="zema-result-preview" aria-labelledby="zema-result-title">
          <div className="zema-home-result-inner zema-wrap">
            <div className="zema-home-result-copy">
              <h2 id="zema-result-title">Результат, с которым можно работать</h2>
              <p className="zema-home-result-lead">Сумма, объект и дата оценки видны сразу. Ниже — аналоги, расчёт и готовое заключение для обсуждения с партнёрами.</p>
              <div className="zema-home-result-summary">
                <span>Бизнес-центр Невский, 68</span>
                <strong>294 000 000 ₽</strong>
                <small>159 000 ₽/м²</small>
              </div>
              <dl>
                <div><dt>Дата оценки</dt><dd>01.01.2025</dd></div>
                <div><dt>Адрес</dt><dd>Санкт-Петербург, Невский проспект, 68</dd></div>
                <div><dt>Заключение</dt><dd>Экран и PDF</dd></div>
              </dl>
              <SampleDownload>Скачать пример заключения</SampleDownload>
            </div>
            <figure className="zema-home-result-paper">
              <img src="/images/report-preview.webp" alt="Первая страница примера предварительного заключения ЗЕМА" width="893" height="1263" />
              <figcaption>Образец PDF по другому объекту · данные приведены в документе</figcaption>
            </figure>
          </div>
        </section>

        <section className="zema-home-closing" aria-labelledby="zema-closing-title">
          <div className="zema-wrap zema-home-closing-inner">
            <div className="zema-home-closing-copy">
              <h2 id="zema-closing-title">Проверьте стоимость объекта до сделки</h2>
              <p>Введите адрес, уточните характеристики и получите предварительный расчёт с рыночными аналогами и готовым PDF.</p>
              <div className="zema-closing-progress" aria-label="Готовность расчёта"><span><i style={{ '--progress': '100%' }} />Адрес найден</span><span><i style={{ '--progress': '72%' }} />Характеристики</span><span><i style={{ '--progress': '46%' }} />Рынок рядом</span></div>
            </div>
            <div className="zema-closing-preview">
              <div className="zema-closing-address"><EnvironmentOutlined /><span><small>Адрес объекта</small><strong>Невский проспект, 68</strong></span></div>
              <dl><div><dt>Площадь</dt><dd>1 850 м²</dd></div><div><dt>Тип</dt><dd>Бизнес-центр</dd></div><div><dt>Срок</dt><dd>от 15 минут</dd></div></dl>
              <div className="zema-actions"><EstimateLink>Начать оценку</EstimateLink><Link className="zema-text-link" to="/contacts">Обсудить объект <ArrowRightOutlined /></Link></div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
